import type { ReactNode } from 'react';

import { createState } from '#lib/signals-utils.js';

export type ToastVariant = 'default' | 'destructive' | 'success';

export interface ToastData {
  id: string;
  title?: ReactNode;
  description?: ReactNode;
  /**
   * Optional action rendered alongside the toast. Typed as the platform-neutral `ReactNode`
   * (rather than the web-only `ToastActionElement` from `#ui/toast`) so this store stays portable
   * to React Native — web passes a `<ToastAction>` element, native passes its own renderable
   * (ELARA-754).
   */
  action?: ReactNode;
  variant?: ToastVariant;
  duration?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

let toastIdCounter = 0;

function generateToastId(): string {
  toastIdCounter = (toastIdCounter + 1) % Number.MAX_SAFE_INTEGER;
  return toastIdCounter.toString();
}

export function createToastState() {
  const state = createState({
    /** Array of active toasts */
    toasts: [] as ToastData[],

    maxToasts: 3,

    defaultDuration: 5000,

    /** Map of toast IDs to their timeout IDs for cleanup. `ReturnType<typeof setTimeout>` keeps this
     * portable — `number` on web/WebWorker, `Timeout` on Node — without depending on `@types/node`. */
    toastTimeouts: new Map<string, ReturnType<typeof setTimeout>>(),

    /** Add a new toast */
    addToast(toast: Omit<ToastData, 'id'>) {
      const id = generateToastId();
      const newToast: ToastData = { id, duration: this.defaultDuration, variant: 'default', ...toast };

      // Add to the beginning and limit the number of toasts
      this.toasts = [newToast, ...this.toasts].slice(0, this.maxToasts);

      // Auto-dismiss if duration is not Infinity
      if (newToast.duration !== Infinity) {
        const timeoutId = setTimeout(() => {
          this.dismissToast(id);
        }, newToast.duration);
        this.toastTimeouts.set(id, timeoutId);
      }

      return id;
    },

    /** Update an existing toast */
    updateToast(id: string, updates: Partial<ToastData>) {
      this.toasts = this.toasts.map((toast) => (toast.id === id ? { ...toast, ...updates } : toast));
    },

    /** Dismiss a specific toast */
    dismissToast(id: string) {
      // Clear any existing timeout for this toast
      const timeoutId = this.toastTimeouts.get(id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.toastTimeouts.delete(id);
      }

      this.toasts = this.toasts.map((toast) => (toast.id === id ? { ...toast, open: false } : toast));

      // Remove from array after a short delay to allow animation
      const removeTimeoutId = setTimeout(() => {
        this.toasts = this.toasts.filter((toast) => toast.id !== id);
        // Clean up the timeout reference after removal
        this.toastTimeouts.delete(id);
      }, 300);

      // Clear any existing timeout before setting the new one to prevent memory leaks
      const existingRemoveTimeout = this.toastTimeouts.get(id);
      if (existingRemoveTimeout) {
        clearTimeout(existingRemoveTimeout);
      }
      this.toastTimeouts.set(id, removeTimeoutId);
    },

    /** Dismiss all toasts */
    dismissAllToasts() {
      // Clear all existing timeouts
      for (const timeoutId of this.toastTimeouts.values()) {
        clearTimeout(timeoutId);
      }
      this.toastTimeouts.clear();

      this.toasts = this.toasts.map((toast) => ({ ...toast, open: false }));

      // Remove all toasts after animation delay
      setTimeout(() => {
        this.toasts = [];
      }, 300);
    },

    /** Remove a toast immediately (for cleanup) */
    removeToast(id: string) {
      // Clear any existing timeout for this toast
      const timeoutId = this.toastTimeouts.get(id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.toastTimeouts.delete(id);
      }

      this.toasts = this.toasts.filter((toast) => toast.id !== id);
    },

    /** Clear all toasts immediately */
    clearAllToasts() {
      // Clear all existing timeouts
      for (const timeoutId of this.toastTimeouts.values()) {
        clearTimeout(timeoutId);
      }
      this.toastTimeouts.clear();

      this.toasts = [];
    },

    /** Get a specific toast by ID */
    getToast(id: string): ToastData | undefined {
      return this.toasts.find((toast) => toast.id === id);
    },

    /** Check if there are any active toasts */
    get hasActiveToasts(): boolean {
      return this.toasts.length > 0;
    },

    /** Get count of active toasts */
    get toastCount(): number {
      return this.toasts.length;
    },

    /** Update toast configuration */
    updateConfig(config: { maxToasts?: number; defaultDuration?: number }) {
      if (config.maxToasts !== undefined) {
        this.maxToasts = config.maxToasts;
      }
      if (config.defaultDuration !== undefined) {
        this.defaultDuration = config.defaultDuration;
      }
    },

    /** Clean up all timeouts (call on component unmount) */
    cleanup() {
      for (const timeoutId of this.toastTimeouts.values()) {
        clearTimeout(timeoutId);
      }
      this.toastTimeouts.clear();
    },

    startMonitors() {
      // No monitors needed for toast state
    },
  });

  return state;
}

export type ToastState = ReturnType<typeof createToastState>;
